// components/ReactionSummary/ReactionSummary.tsx
// Ícones sobrepostos estilo Facebook — passa reaction_summary do post

import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import "./reaction-summary.css";
import { formatNumber } from "../../utils/numbers";
import { useReactionTypes } from "../../contexts/ReactionsContext";

interface Props {
    slugs: string[] | string;
    totalLikes: number;
    onClick?: () => void;
}

// Mantém a renderização correta mesmo enquanto os tipos de reação ainda
// estão sendo carregados pela API. Os valores vindos da API têm prioridade.
const FALLBACK_EMOJIS: Record<string, string> = {
    ball: "⚽",
    fire: "🔥",
    light: "💡",
    cry: "😭",
    laughter: "😂",
    wow: "😮",
    clown: "🤡",
};

const normalizeSlugs = (slugs: string[] | string | null | undefined): string[] => {
    if (Array.isArray(slugs)) return slugs;
    if (typeof slugs !== "string") return [];

    try {
        const parsed = JSON.parse(slugs);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const ReactionIcon: React.FC<{ slug: string }> = ({ slug }) => {
    const reactionTypes = useReactionTypes();

    if (slug === "ball") {
        return <img src="/img/reacts/ball.png" className="rsm-img" alt="bola" />;
    }

    if (slug === "like") {
        return (
            <span className="rsm-heart">
                <Heart fill="#e0245e" color="#e0245e" size={13} />
            </span>
        );
    }

    const emoji = reactionTypes.find(reaction => reaction.slug === slug)?.emoji
        ?? FALLBACK_EMOJIS[slug]
        ?? "👍";

    return <span className="rsm-emoji" aria-label={slug}>{emoji}</span>;
};

const ReactionSummary: React.FC<Props> = ({ slugs, totalLikes, onClick }) => {
    if (totalLikes === 0) return null;

    const unique = Array.from(new Set(normalizeSlugs(slugs)))
        .slice(0, Math.min(3, totalLikes));

    const [ready, setReady] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setReady(true), 300);
        return () => clearTimeout(t);
    }, []);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!ready) return;
        if (totalLikes > 0) onClick?.();
    };

    return (
        <span
            className={`rsm-wrapper${onClick ? " rsm-clickable" : ""}`}
            title={`${totalLikes} reações`}
            onClick={handleClick}
        >
            <span className="rsm-icons">
                {!unique.length && (
                    <span
                        key="like"
                        className="rsm-icon-bubble"
                        style={{ zIndex: 1 }}
                    >
                        <ReactionIcon slug="like" />
                    </span>
                )}
                {unique.map((slug, i) => (
                    <span
                        key={slug}
                        className="rsm-icon-bubble"
                        style={{ zIndex: unique.length - i, marginLeft: i === 0 ? 0 : -6 }}
                    >
                        <ReactionIcon slug={slug} />
                    </span>
                ))}

                <span
                    style={{
                        minWidth: 12,
                        marginLeft: 3,
                        paddingRight: 5,
                        cursor: totalLikes > 0 ? "pointer" : "default",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                    }}
                >
                    {totalLikes > 0 ? formatNumber(totalLikes) : <>&nbsp;</>}
                </span>
            </span>
        </span>
    );
};

export default ReactionSummary;
